// Wraps the color picker component to hold the selection state before passing to the editor
import { useState } from "react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
} from "@headlessui/react";
import { HexColorInput, HexColorPicker } from "react-colorful";
import { Editor } from "@tiptap/react";
import { TextSelection } from "@tiptap/pm/state";

import { Button } from "../../../../common/Atoms/Button";
import { Paragraph } from "../../../../common/Atoms/Typography/Paragraph";

import { EditorIcon } from "./EditorIcon";

interface ColorPickerEditorProps {
  editor?: Editor | null;
}

export function ColorPickerEditor({
  editor,
}: ColorPickerEditorProps) {
  const [color, setColor] = useState<string | null>(null);

  function onConfirm() {
    if (editor) {
      editor
        .chain()
        .focus()
        .command(({ state, dispatch }) => expandSelectionToIncludeMergeTags({ state, dispatch }))
        .setColor(color)
        .run(); // Running this closes the popover
    }
  }

  function removeColor() {
    if (editor) {
      editor
        .chain()
        .focus()
        .command(({ state, dispatch }) => expandSelectionToIncludeMergeTags({ state, dispatch }))
        .unsetColor()
        .run();
    }
  }

  return (
    <div>
      <Popover
        className={ `relative` }
      >
        <PopoverButton
          title={ `Text colour` }
          aria-label={ `Text colour` }
          className={ `flex h-7 w-7 flex-col items-center justify-center gap-px rounded-[5px] text-paidniceGreen-100 hover:bg-paidniceGreen-700` }
        >
          <EditorIcon name={ `textColour` }
            size={ 13 } />
          <span
            className={ `h-[3px] w-[13px] rounded-[1px]` }
            style={ { background: editor?.getAttributes(`textStyle`)?.color || `#80cbc4` } } />
        </PopoverButton>

        <PopoverPanel
          className={ `absolute z-50 left-0 top-full mt-2 bg-white border border-gray-400 rounded-lg` }
          focus
        >
          <HexColorPicker
            color={ color || `` }
            onChange={ setColor }
          />
          <HexColorInput
            color={ color || `` }
            onChange={ setColor }
            prefixed
            className={ `w-full bg-slate-50 mt-2 p-2 border-b border-gray-400` }
          />

          <div className={ `w-full py-2 px-1` }>
            <div
              className={ `flex justify-center space-x-2` }
            >
              <Button
                onClick={ onConfirm }
              >
                { `Apply` }
              </Button>
              <Button
                onClick={ removeColor }
                color={ `gray` }
              >
                { `Clear` }
              </Button>
            </div>
            <Paragraph
              variant={ `help` }
              className={ `text-center` }
            >
              { `Applies to the selected text` }
            </Paragraph>
          </div>
        </PopoverPanel>
      </Popover>
    </div>
  );
}

// Utility: expand selection to full {{{MergeTag}}} if selection is inside one
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const expandSelectionToIncludeMergeTags = ({ state, dispatch }: any) => {
  const { selection } = state;

  if (!(selection instanceof TextSelection)) return false;

  const { $from, $to } = selection;

  // Only handle simple case where selection is in one parent node
  if (!$from.sameParent($to)) return false;

  const parent = $from.parent;
  const parentText = parent.textBetween(0, parent.content.size, `\0`, `\0`);

  const selFrom = $from.parentOffset;
  const selTo = $to.parentOffset;

  let newFrom = selFrom;
  let newTo = selTo;

  // Find all {{{...}}} tags in this parent text
  const re = /\{\{\{[^}]+\}\}\}/g;
  let match: RegExpExecArray | null;

  while ((match = re.exec(parentText))) {
    const start = match.index;
    const end = start + match[0].length;

    // If the current selection overlaps this tag at all...
    const overlaps = selFrom < end && selTo > start;
    if (!overlaps) continue;

    // Expand the selection to fully cover this tag as well,
    // but never shrink the original selection.
    if (start < newFrom) newFrom = start;
    if (end > newTo) newTo = end;
  }

  // Nothing to change
  if (newFrom === selFrom && newTo === selTo) return false;

  if (!dispatch) return true;

  const absFrom = $from.start() + newFrom;
  const absTo = $from.start() + newTo;

  const tr = state.tr.setSelection(
    TextSelection.create(state.doc, absFrom, absTo),
  );
  dispatch(tr);

  return true;
};

